<template>
  <a-layout-sider
    :width="settings.leftMenuWidth"
    :collapsible="settings.isMobileView ? false : true"
    :collapsed="settings.isMenuCollapsed && !settings.isMobileView"
    :class="{
      [$style.menu]: true,
      [$style.gray]: true,
    }"
    @collapse="onCollapse"
  >
    <div
      :class="$style.menuOuter"
      :style="{
        width:
          settings.isMenuCollapsed && !settings.isMobileView
            ? '80px'
            : settings.leftMenuWidth + 'px',
        height:
          settings.isMobileView || settings.isMenuUnfixed
            ? 'calc(100% - 64px)'
            : 'calc(100% - 110px)',
      }"
    >
      <div :class="$style.logoContainer">
        <div :class="$style.logo">
          <router-link to="/">
            <img
              v-if="!settings.isMenuCollapsed"
              width="80"
              src="../../../assets/img/logo_nodecenter.png"
            />
            <img v-else width="45" src="/img/favicon.png" />
          </router-link>
        </div>
      </div>
      <perfect-scrollbar :style="{ height: 'calc(100% - 110px)', overflow: 'auto' }" class="mt-4">
        <a-menu
          v-model:open-keys="openKeys"
          :collapsed="settings.isMobileView ? false : settings.isMenuCollapsed"
          :mode="'inline'"
          :selected-keys="selectedKeys"
          :inline-indent="15"
          :class="$style.navigation"
          @click="handleClick"
          @openChange="handleOpenChange"
        >
          <template v-for="item in filteredMenu">
            <template v-if="item.category">
              <a-menu-item-group :key="item.key">
                <template #title>
                  {{ item.title }}
                </template>
              </a-menu-item-group>
            </template>
            <template v-else-if="item.children && item.children.length > 0">
              <sub-menu :key="item.key" :menu-info="item" :styles="$style" />
            </template>
            <template v-else>
              <item :key="item.key" :menu-info="item" :styles="$style" />
            </template>
          </template>
        </a-menu>
      </perfect-scrollbar>
    </div>
  </a-layout-sider>
</template>

<script>
import { ref, onMounted, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { getMenuData } from '@/services/menu'
import SubMenu from './partials/submenu'
import Item from './partials/item'
import { getAssetTypeCategory } from '../../../services/axios/modc/assets'

export default {
  name: 'MenuLeft',
  components: { SubMenu, Item },
  setup() {
    const store = useStore()
    const route = useRoute()

    const staticMenuData = getMenuData
    const dynamicAssetCategories = ref([])

    // Roles that can see the dynamic asset management menus
    const assetViewerRoles = ['admin', 'siteManager', 'dcOwner']

    const combinedMenu = computed(() => {
      // 1. Create dynamic menus from API response
      const dynamicMenuTransformed = dynamicAssetCategories.value.map(category => {
        const categorySlug = category.name
          .toLowerCase()
          .replace(/ /g, '_')
          .replace('.', '')
        const menuChildren = category.AssetSubCategories.map(subCategory => ({
          title: subCategory.name,
          key: `dynamic_${categorySlug}_${subCategory.id}`,
          url: `/assets/${subCategory.id}`,
        }))

        let icon = 'fe fe-box' // Default icon
        // Assign icons based on category ID
        switch (category.id) {
          case 1:
            icon = 'fe fe-cpu'
            break
          case 2:
            icon = 'fe fe-speaker'
            break
          case 3:
            icon = 'fe fe-printer'
            break
          case 4:
            icon = 'fe fe-hard-drive'
            break
          case 5:
            icon = 'fe fe-home'
            break
        }

        return {
          title: category.name,
          key: `dynamic_${categorySlug}_main`,
          url: undefined, // Parent is not clickable
          icon: icon,
          children: menuChildren,
          roles: assetViewerRoles, // Assign roles to the dynamic parent menu
        }
      })

      // 2. Clone base menu to avoid direct mutation
      let baseMenu = JSON.parse(JSON.stringify(staticMenuData))

      // 4. Create the header for the dynamic asset section
      const dynamicAssetCategoryHeader = {
        category: true,
        title: 'MANAJEMEN ASET DINAMIS',
        key: 'category_dynamic_assets',
        roles: assetViewerRoles, // Assign roles to the category header
      }

      // 5. Combine all menu parts
      return [...baseMenu, dynamicAssetCategoryHeader, ...dynamicMenuTransformed]
    })

    let filteredMenu = ref([])
    let selectedKeys = ref([])
    let openKeys = ref([])
    let settings = computed(() => store.getters.settings)
    let isMenuCollapsed = computed(() => store.getters.isMenuCollapsed)
    let user = computed(() => store.getters['user/user'])
    let currentPath = computed(() => route.path)

    const onCollapse = (collapsed, type) => {
      const value = !settings.value.isMenuCollapsed
      store.commit('CHANGE_SETTING', { setting: 'isMenuCollapsed', value })
    }

    const handleClick = e => {
      if (e.key === 'settings') {
        store.commit('CHANGE_SETTING', { setting: 'isSettingsOpen', value: true })
        return
      }
    }

    const handleOpenChange = newOpenKeys => {
      openKeys.value = newOpenKeys
    }

    /**
     * Recursively filters a menu array based on the user's role.
     * @param {Array} menuData - The array of menu items to filter.
     * @param {String} userRole - The role of the current user.
     * @returns {Array} - The filtered menu array.
     */
    const filterMenuByRole = (menuData, userRole) => {
      return menuData.reduce((acc, item) => {
        // A menu item is accessible if it has no roles defined (public for all)
        // or if the user's role is included in its roles array.
        const isAccessible = !item.roles || item.roles.includes(userRole)

        if (isAccessible) {
          const newItem = { ...item }

          // If the item has children, filter them recursively
          if (item.children) {
            newItem.children = filterMenuByRole(item.children, userRole)
            // A parent menu should only be visible if it has children left after filtering,
            // unless the parent itself is a clickable link.
            if (newItem.children.length > 0 || newItem.url) {
              acc.push(newItem)
            }
          } else {
            // If it's a regular item with no children, just add it.
            acc.push(newItem)
          }
        }
        return acc
      }, [])
    }

    const applyRoleFilter = () => {
      const currentUserRole = user.value?.role || 'guest'
      filteredMenu.value = filterMenuByRole(combinedMenu.value, currentUserRole)
    }

    const setSelectedKeys = () => {
      let bestMatchKey = null
      let bestMatchParents = []
      let bestMatchUrlLength = 0

      const findMatchingKeyAndParents = (items, currentParents = []) => {
        for (const item of items) {
          if (item.url) {
            const itemUrlNormalized = item.url.endsWith('/') ? item.url.slice(0, -1) : item.url
            const currentPathNormalized = currentPath.value.endsWith('/')
              ? currentPath.value.slice(0, -1)
              : currentPath.value

            if (currentPathNormalized === itemUrlNormalized) {
              bestMatchKey = item.key
              bestMatchParents = [...currentParents]
              return true // Exact match found, stop searching
            }

            if (
              currentPathNormalized.startsWith(itemUrlNormalized) &&
              itemUrlNormalized.length > bestMatchUrlLength
            ) {
              bestMatchKey = item.key
              bestMatchParents = [...currentParents]
              bestMatchUrlLength = itemUrlNormalized.length
            }
          }
          if (
            item.children &&
            findMatchingKeyAndParents(item.children, [...currentParents, item.key])
          ) {
            return true
          }
        }
        return false
      }

      findMatchingKeyAndParents(filteredMenu.value) // Important: Search on the already filtered menu

      selectedKeys.value = bestMatchKey ? [bestMatchKey] : []
      openKeys.value = bestMatchKey ? bestMatchParents : []
    }

    const fetchAssetMenu = async () => {
      try {
        const response = await getAssetTypeCategory()
        dynamicAssetCategories.value = response.categories || []
      } catch (error) {
        console.error('Gagal memuat kategori aset untuk menu:', error)
      } finally {
        // Apply filter and selection AFTER data is loaded and combined
        applyRoleFilter()
        setSelectedKeys()
      }
    }

    onMounted(fetchAssetMenu)

    // Watch for changes and re-filter/re-select accordingly
    watch(
      () => user.value?.role,
      () => {
        applyRoleFilter()
        setSelectedKeys()
      },
    )
    watch(currentPath, setSelectedKeys)
    watch(isMenuCollapsed, collapsed => {
      if (collapsed) {
        openKeys.value = []
      } else {
        setSelectedKeys()
      }
    })

    return {
      selectedKeys,
      openKeys,
      settings,
      user,
      onCollapse,
      handleClick,
      handleOpenChange,
      filteredMenu,
    }
  },
}
</script>

<style lang="scss" module>
@import './style.module.scss';
</style>
